import { ClipboardList, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useAuth } from "@/auth";
import { toast } from "sonner";

import { api } from "@/api";
import { MicButton } from "@/components/MicButton";
import { errText } from "@/components/pipeline/utils";

const pad = (v) => (/^\d$/.test(v) ? `0${v}` : v);
// Même format que les comptes rendus déjà saisis : « 4. Nombre d'écoles maternelles *: 01 »
const toComment = (script, answers) => script.map((b) => [b.title, "", ...b.questions.filter((q) => String(answers[q.label] ?? "").trim()).map((q) => `${q.n ? `${q.n}. ` : ""}${q.label}${q.required ? " *" : ""}: ${q.type === "number" ? pad(String(answers[q.label])) : answers[q.label]}`)].join("\r\n")).join("\r\n\r\n");

const norm = (t) => (t || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const LINE = /^[^\p{L}\d\n]*(?:\d+\s*[./)]\s*)?([^:\n]{3,90}?)\s*\*?\s*:\s*(.+?)\s*$/gmu;
// Réponses déjà connues : champs de la fiche, puis dernier compte rendu au format « question : réponse »
const known = (script, fields, comments) => {
  const out = {};
  const qs = script.flatMap((b) => b.questions);
  for (const c of comments || []) {
    for (const [, k, v] of (c.content || "").matchAll(LINE)) {
      const q = qs.find((x) => norm(x.label) === norm(k));
      if (q && !(q.label in out) && v.trim()) out[q.label] = q.options?.find((o) => norm(o) === norm(v) || norm(o).startsWith(norm(v))) || v.trim();
    }
  }
  qs.forEach((q) => { if (q.field && fields?.[q.field] && !(q.label in out)) out[q.label] = String(fields[q.field]); });
  const age = (v) => { const y = String(v).match(/^(1[89]|20)\d{2}$/); const n = y ? new Date().getFullYear() - Number(v) : Number((norm(v).match(/\d+/) || [])[0]); return Number.isNaN(n) ? null : n; };
  qs.forEach((q) => {
    if (!q.options?.includes("Plus de 15 ans") || !out[q.label] || q.options.includes(out[q.label])) return;
    const n = age(out[q.label]);
    if (n != null) out[q.label] = !/moins/i.test(out[q.label]) && n >= 15 ? "Plus de 15 ans" : "Moins de 15 ans";
  });
  qs.forEach((q) => { if (q.options && out[q.label]) out[q.label] = q.options.find((o) => norm(o) === norm(out[q.label]) || norm(o).startsWith(norm(out[q.label]))) || out[q.label]; });
  return out;
};

const Question = ({ q, value, set }) => (
  <label className={`ldx-q${q.required && !String(value ?? "").trim() ? " is-req" : ""}`}>
    <span>{q.n ? `${q.n}. ` : ""}{q.label}{q.required && " *"}</span>
    {q.options ? (
      <span className="ldx-quick">{[...q.options, ...(value && !q.options.includes(value) ? [value] : [])].map((o) => <button key={o} type="button" className="chip" aria-pressed={value === o} onClick={() => set(o)} data-testid={`script-opt-${q.label.replace(/\W+/g, "-")}-${o.replace(/\W+/g, "-")}`}>{o}</button>)}</span>
    ) : (
      <span className="ldx-q__in">
        <input type={q.type === "number" ? "number" : q.type || "text"} min="0" value={value ?? ""} placeholder={q.placeholder || "—"} onChange={(e) => set(e.target.value)} data-testid={`script-input-${q.label.replace(/\W+/g, "-")}`} />
        {q.type !== "number" && <MicButton label="" onText={(t) => set(value ? `${value} ${t}` : t)} testid={`script-mic-${q.label.replace(/\W+/g, "-")}`} />}
      </span>
    )}
  </label>
);

// Script guidé du pipeline, rempli pendant l'appel : publié en commentaire, il remplit tout seul les champs de la fiche
export const GuidedScript = ({ leadId, script, email, onComment, prefill, fields, comments }) => {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState(() => ({ "Votre Email": email || "", ...(prefill ? known(script, fields, comments) : {}) }));
  const [busy, setBusy] = useState(false);
  const qs = script.flatMap((b) => b.questions);
  const set = (label, v) => setAnswers((a) => {
    const next = { ...a, [label]: v };
    const total = qs.find((q) => q.total);
    if (total && qs.find((q) => q.label === label)?.sum) next[total.label] = String(qs.filter((q) => q.sum).reduce((n, q) => n + (Number(next[q.label]) || 0), 0));
    return next;
  });
  const missing = qs.filter((q) => q.required && !String(answers[q.label] ?? "").trim()).length;
  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pipeline/leads/${leadId}/comments`, { content: toComment(script, answers) });
      onComment(data);
      const n = Object.keys(data.filled || {}).length;
      toast.success(`Script publié${n ? ` · ${n} champ${n > 1 ? "s" : ""} rempli${n > 1 ? "s" : ""}` : ""}.`);
      setOpen(false);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const { user } = useAuth();
  const status = missing ? `${missing} réponse${missing > 1 ? "s" : ""} obligatoire${missing > 1 ? "s" : ""}` : "Complet";
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);
  return (
    <section className="ldx-script-card" data-testid="guided-script">
      <button type="button" className="ldx-script-big" onClick={() => setOpen(true)} data-testid="guided-script-toggle">
        <ClipboardList size={22} /><b>Ouvrir le script de qualification</b><small data-testid="guided-script-status">{status}</small>
      </button>
      {open && createPortal(
        <div className="ldx-smodal-back" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)} data-testid="guided-script-backdrop">
          <div className="ldx-smodal" role="dialog" aria-modal="true" aria-label="Script de qualification" data-testid="guided-script-modal">
            <header className="ldx-smodal__head">
              <div><h2>Script de qualification</h2><p>{status} · les réponses remplissent la fiche{user?.role === "admin" && <> · <a href="/parametres-pipeline#scripts" data-testid="guided-script-edit-link">Modifier les scripts</a></>}</p></div>
              <button type="button" className="ldx-smodal__close" onClick={() => setOpen(false)} aria-label="Fermer" data-testid="guided-script-close"><X size={18} /></button>
            </header>
            <div className="ldx-script-body">
              {script.map((b, i) => <div key={b.title} className="ldx-sblock"><h4>{b.title}</h4>{b.say && <details className="ldx-say" open={i === 0} data-testid={`script-say-${i}`}><summary>Texte à dire</summary><p>{b.say}</p></details>}{b.questions.map((q) => <Question key={q.label} q={q} value={answers[q.label]} set={(v) => set(q.label, v)} />)}</div>)}
            </div>
            <footer className="ldx-smodal__foot">
              <button type="button" className="btn btn--sm btn--ink" disabled={busy || missing > 0} onClick={publish} data-testid="guided-script-publish">{busy ? "Publication…" : "Publier le script et remplir la fiche"}</button>
            </footer>
          </div>
        </div>,
        document.querySelector(".app-shell") || document.body,
      )}
    </section>
  );
};
